import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {useNavigate, useParams} from 'react-router-dom'
import {GET, PUT, POST} from '../../../services/api'
import {Input} from '../../../components/Input/Input'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {Loading} from '../../../components/Loading/Loading'
import {Button} from '../../../components/Button/Button'
import {i18n} from '../../../translate/i18n'
import Swal from 'sweetalert2'
import uuid from 'react-uuid'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'

export enum VinculationType {
  DEFAULT = 'default',
  DYNAMIC = 'dynamic',
}

const HotelEditStore: FC = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [children, setChildren] = useState<any[]>([])
  const [selectedChildId, setSelectedChildId] = useState<number | string | null>(null)
  const [link, setLink] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [status, setStatus] = useState<string>('inactive')
  const [cnpj, setCNPJ] = useState<string>('')
  const [storeCode, setStoreCode] = useState<string | null>(null)
  const [storeType, setStoreType] = useState<string | null>(null)
  const [order, setOrder] = useState<string>('')
  const [paymentIntegrated, setPaymentIntegrated] = useState<string>('inactive')
  const [vinculationType, setVinculationType] = useState<VinculationType>(VinculationType.DEFAULT)
  const [img, setImg] = useState<File | string | null>(null)
  const [originalImg, setOriginalImg] = useState<string | null>(null)

  const getStores = async () => {
    try {
      const response = await GET(`/store-relations/children`)
      if (response) {
        const stores = response.data.filter(
          (store: any) => store.childPluginspace.type !== 'hospitality'
        )
        setChildren(stores)
        return stores
      }
    } catch (error) {
      console.log(error)
      setChildren([])
      return []
    }
    return []
  }

  const getStore = async (availableChildren?: any[]) => {
    setLoading(true)
    try {
      const res = await GET(`/stores-hotel/${id}`)
      const data = res?.data
      if (!data) return

      setLink(data.link ?? '')
      setDescription(data.description ?? '')
      setStatus(data.status ? 'active' : 'inactive')
      setStoreCode(data.code ?? null)
      setOrder(data.order !== undefined && data.order !== null ? String(data.order) : '')
      setPaymentIntegrated(data.paymentIntegrated ? 'active' : 'inactive')
      setImg(data.img ?? null)
      setOriginalImg(data.img ?? null)
      setVinculationType(data.vinculationType ? data.vinculationType : VinculationType.DEFAULT)

      const childrenList = availableChildren ?? children
      const code = data.code ?? null

      const matched = code ? childrenList.find((c: any) => String(c.child) === String(code)) : null

      setSelectedChildId(matched?.child ?? null)
      setCNPJ(matched?.childPluginspace.cnpj ?? '')
      setStoreType(matched?.childPluginspace.type ?? null)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const ensureDescription = (html: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const text = doc.body.textContent?.trim() || ''

    return text === '' ? '' : html.trim()
  }

  const init = async () => {
    setLoading(true)
    const stores = await getStores()
    await getStore(stores)
    setLoading(false)
  }

  useEffect(() => {
    if (id) {
      init()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleSave = async () => {
    try {
      setLoading(true)
      console.log(link)

      const body = {
        link: link || '',
        description: ensureDescription(description) || '',
        status: status === 'active' ? true : false,
        order: order !== undefined && order !== null && order !== '' ? Number(order) : undefined,
        paymentIntegrated: paymentIntegrated === 'active' ? true : false,
        vinculationType,
      }

      const res = await PUT(`/stores-hotel/${id}`, body)
      if (res && res.success) {
        // If user removed the existing image (cleared it), persist the removal
        if (img === null && originalImg) {
          try {
            await PUT(`/stores-hotel/${id}`, {img: null})
            setOriginalImg(null)
          } catch (err) {
            console.log(
              String(i18n.t('stores_pages_edit.console_messages.image_remove_error')),
              err
            )
          }
        }

        // If user selected a new image (File), upload and attach it
        if (img && typeof img !== 'string') {
          try {
            const form = new FormData()
            form.append('file', img)
            form.append('nameImage', `store-${uuid()}.jpg`)
            form.append('originalSize', 'true')
            const imageRes = await POST('/upload-images/upload-one', form)
            if (imageRes && imageRes.success) {
              await PUT(`/stores-hotel/${id}`, {img: imageRes.image})
            }
          } catch (err) {
            console.log(
              String(i18n.t('stores_pages_edit.console_messages.image_upload_error')),
              err
            )
          }
        }

        Swal.fire({
          icon: 'success',
          title: String(i18n.t('global.success')),
          text: String(i18n.t('stores_pages_edit.messages.success_updated')),
        }).then(() => {
          navigate('/hospitality/configuracoes/stores')
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('global.error')),
          text:
            (res && res.error && res.error.message) ||
            String(i18n.t('stores_pages_edit.messages.error_updating')),
        })
      }
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentIntegratedChange = (newValue: any) => {
    if (newValue === paymentIntegrated) return

    Swal.fire({
      title: String(i18n.t('global.warning')),
      text: String(i18n.t('stores_pages_edit.messages.payment_change_warning_text')),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: String(i18n.t('stores_pages_edit.messages.payment_confirm')),
      cancelButtonText: String(i18n.t('stores_pages_edit.messages.cancel')),
    }).then((result) => {
      if (result.isConfirmed) {
        setPaymentIntegrated(newValue)
      }
    })
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('stores_pages_edit.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('stores_pages_edit.subtitle')}
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <div className='row'>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'select'}}
                    label={i18n.t('stores_pages_edit.form.establishment')}
                    placeholder={i18n.t('stores_pages_edit.form.select_placeholder')}
                    search={true}
                    options={children.map((c) => ({value: c.id, select: c.name}))}
                    value={
                      children.find((c) => c.id === selectedChildId)
                        ? {
                            value: selectedChildId,
                            label: children.find((c) => c.id === selectedChildId)?.name,
                          }
                        : ''
                    }
                    disabled={true}
                    required={true}
                  />
                </div>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label={i18n.t('stores_pages_edit.form.link')}
                    placeholder={i18n.t('stores_pages_edit.form.link_placeholder')}
                    value={link}
                    change={(value: string) => setLink(value)}
                    maxCaracteres={200}
                  />
                </div>

                <div className='row'>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label={i18n.t('stores_pages_edit.form.status')}
                      options={[
                        {
                          value: 'active',
                          select: i18n.t('stores_pages_edit.form.status_options.active'),
                        },
                        {
                          value: 'inactive',
                          select: i18n.t('stores_pages_edit.form.status_options.inactive'),
                        },
                      ]}
                      value={status}
                      change={(v: any) => setStatus(v)}
                      required={true}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label={i18n.t('stores_pages_edit.form.payment_integrated')}
                      options={[
                        {
                          value: 'active',
                          select: i18n.t('stores_pages_edit.form.payment_options.active'),
                        },
                        {
                          value: 'inactive',
                          select: i18n.t('stores_pages_edit.form.payment_options.inactive'),
                        },
                      ]}
                      value={paymentIntegrated}
                      change={(v: any) => handlePaymentIntegratedChange(v)}
                      required={true}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'default'}}
                      label={i18n.t('stores_pages_edit.form.order')}
                      value={order}
                      placeholder={i18n.t('stores_pages_edit.placeholders.order')}
                      change={(v: any) => setOrder(v)}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label={i18n.t('stores_pages_edit.form.vinculation_label')}
                      options={[
                        {
                          value: 'default',
                          select: i18n.t('stores_pages_edit.form.vinculation_options.default'),
                        },
                        {
                          value: 'dynamic',
                          select: i18n.t('stores_pages_edit.form.vinculation_options.dynamic'),
                        },
                      ]}
                      value={vinculationType}
                      required={true}
                      change={(v: VinculationType) => setVinculationType(v)}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'image'}}
                    label={i18n.t('stores_pages_edit.form.image_label')}
                    value={img}
                    change={(value: any) => {
                      if (value === null) {
                        setImg(null)
                        return
                      }
                      if (value instanceof File) {
                        setImg(value)
                        return
                      }
                      const file = value?.target?.files && value.target.files[0]
                      setImg(file ?? null)
                    }}
                    paddingLeft='0'
                    marginTop='mt-6'
                    smalltext={i18n.t('stores_pages_edit.form.image_smalltext')}
                  />
                </div>
              </div>

              <div className='row mt-6'>
                <div className='col-md-12'>
                  <label className='form-label fw-normal'>
                    {i18n.t('stores_pages_edit.form.description')}
                  </label>
                  <EditorQuill
                    value={description}
                    change={(v: any) => setDescription(v)}
                    maxLength={100}
                  />
                </div>
              </div>

              <div className='row mt-6'>
                <div className='col-12'>
                  <h5 className='mb-4'>{i18n.t('stores_pages_edit.form.info_title')}</h5>
                </div>

                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'cnpj'}}
                    label={i18n.t('stores_pages_edit.form.cnpj')}
                    value={cnpj}
                    placeholder={i18n.t('stores_pages_edit.placeholders.cnpj')}
                    change={(v: any) => setCNPJ(v)}
                    disabled={true}
                  />
                </div>

                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label={i18n.t('stores_pages_edit.form.code')}
                    value={storeCode || ''}
                    placeholder={i18n.t('stores_pages_edit.placeholders.store_id')}
                    change={(v: any) => setStoreCode(v)}
                    disabled={true}
                  />
                </div>

                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label={i18n.t('stores_pages_edit.form.type')}
                    value={storeType || ''}
                    placeholder={i18n.t('stores_pages_edit.form.type')}
                    change={(v: any) => setStoreType(v)}
                    disabled={true}
                  />
                </div>
              </div>

              {children.length === 0 && (
                <div className='text-center text-muted mt-4'>
                  {i18n.t('stores_pages_edit.form.empty_message')}
                </div>
              )}
            </div>

            <div className='card-footer border-0 pt-0 d-flex justify-content-between'>
              <Button
                text={i18n.t(`buttons.back`)}
                color='info'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => navigate('/hospitality/configuracoes/stores')}
                btnClass='fw-bolder'
              />
              <Button
                text={i18n.t(`buttons.save`)}
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                btnClass='fw-bolder'
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const HotelEditStores: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('permission_routes.dashboard'), path: 'dashboard', isActive: false},
          {
            title: i18n.t('stores_pages_edit.title'),
            path: 'hospitality/configuracoes/stores/edit/:id',
            isActive: true,
          },
        ]}
      >
        {i18n.t('stores_pages_edit.title')}
      </PageTitle>
      <HotelEditStore />
    </>
  )
}

export {HotelEditStores}
